import React, { useMemo, ReactElement } from 'react';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import RequiredMark from '../../../components/required-mark/required-mark.component';
import FormFieldSelect from '../../../components/form-field-select/form-field-select.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import FormFieldNumber from '../../../components/form-field-number/form-field-number.component';
import FormFieldDate from '../../../components/form-field-date/form-field-date.component';
import MenuItemList from '../../../components/menu-item-list/menu-item-list.component';
import SinglePageTab from '../../app/common/single-page-tab/single-page-tab.component';
import FormatService from '../../../services/format-service';

const AssetInvestmentPerformance: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isSaving,
    isSaveDisabled,
    isEditMode,
    isRightDrawerOpen,
    formError,
    assetData,
    assetStatusList,
    onCancelForm,
    onSubmitForm,
  } = props;

  const formPrefix: string = 'assetData';
  const parsedMaturityDate: Date = assetData && assetData.maturityDate && new Date(assetData.maturityDate);

  const maturityDate: string = useMemo((): string => (
    parsedMaturityDate && FormatService.formatDateString(parsedMaturityDate)
  ), [parsedMaturityDate]);

  const menuItemAssetStatusList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: assetStatusList,
  }), [assetStatusList]);

  return (
    <SinglePageTab
      isSaving={isSaving}
      isSaveDisabled={isSaveDisabled}
      isEditMode={isEditMode}
      isRightDrawerOpen={isRightDrawerOpen}
      formError={formError}
      onCancelForm={onCancelForm}
      onSubmitForm={onSubmitForm}
    >
      <Table>
        <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
          <TableRow>
            <TableCell>
              Status
              {
                isEditMode && (
                  <RequiredMark />
                )
              }
            </TableCell>
            <TableCell>
              {
                isEditMode ? (
                  <FormFieldSelect
                    isBordered
                    isDisabled={assetStatusList && !assetStatusList.length}
                    name={`${formPrefix}.status`}
                    optionsTotal={assetStatusList && assetStatusList.length}
                  >
                    {menuItemAssetStatusList}
                  </FormFieldSelect>
                ) : assetData.statusText
              }
            </TableCell>
          </TableRow>
          {
            (isEditMode || maturityDate) && (
              <TableRow>
                <TableCell>
                  Maturity Date
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldDate isBordered withBlankValues name={`${formPrefix}.maturityDate`} />
                    ) : maturityDate
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || assetData.leveredIrr) && (
              <TableRow>
                <TableCell>
                  Levered IRR
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldNumber isFloat isPercent isBordered name={`${formPrefix}.leveredIrr`} />
                    ) : assetData.leveredIrr
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || assetData.equityMultiple) && (
              <TableRow>
                <TableCell>
                  Equity Multiple
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldNumber isFloat isBordered name={`${formPrefix}.equityMultiple`} />
                    ) : assetData.equityMultiple
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || assetData.investmentEntityName) && (
              <TableRow>
                <TableCell>
                  Investment Entity Name
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText isBordered name={`${formPrefix}.investmentEntityName`} />
                    ) : assetData.investmentEntityName
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || assetData.investmentAmountCommitted) && (
              <TableRow>
                <TableCell>
                  Investment Amount Committed
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldNumber isBordered name={`${formPrefix}.investmentAmountCommitted`} />
                    ) : assetData.investmentAmountCommitted
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || assetData.investmentAmountUnfunded) && (
              <TableRow>
                <TableCell>
                  Investment Amount Unfunded
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldNumber isBordered name={`${formPrefix}.investmentAmountUnfunded`} />
                    ) : assetData.investmentAmountUnfunded
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || assetData.investmentAmountFunded) && (
              <TableRow>
                <TableCell>
                  Investment Amount Funded
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldNumber isBordered name={`${formPrefix}.investmentAmountFunded`} />
                    ) : assetData.investmentAmountFunded
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || assetData.currentMarketValue) && (
              <TableRow>
                <TableCell>
                  Current Market Value
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldNumber isBordered name={`${formPrefix}.currentMarketValue`} />
                    ) : assetData.currentMarketValue
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || assetData.closeDate) && (
              <TableRow>
                <TableCell>
                  Close Date
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldDate isBordered withBlankValues name={`${formPrefix}.closeDate`} />
                    ) : assetData.closeDate
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || assetData.extensions) && (
              <TableRow>
                <TableCell>
                  Extensions
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText isBordered name={`${formPrefix}.extensions`} />
                    ) : assetData.extensions
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || assetData.notePrincipalBalanceAtOrigination) && (
              <TableRow>
                <TableCell>
                  Note Principal Balance at Origination
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldNumber isBordered name={`${formPrefix}.notePrincipalBalanceAtOrigination`} />
                    ) : assetData.notePrincipalBalanceAtOrigination
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || assetData.accruedInterest) && (
              <TableRow>
                <TableCell>
                  Accrued Interest
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldNumber isBordered name={`${formPrefix}.accruedInterest`} />
                    ) : assetData.accruedInterest
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || assetData.yardiAssetId) && (
              <TableRow>
                <TableCell>
                  Yardi Asset ID
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText isBordered name={`${formPrefix}.yardiAssetId`} />
                    ) : assetData.yardiAssetId
                  }
                </TableCell>
              </TableRow>
            )
          }
        </TableBody>
      </Table>
    </SinglePageTab>
  );
});

export default AssetInvestmentPerformance;
